import { addDisplayName, addProps, flowMax } from 'ad-hok'
import cx from 'classnames'
import { take } from 'lodash/fp'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { UserListPublic } from 'shared/src/api/types'

import { Button } from 'components/Button'
import { Creator } from 'components/Creator'
import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { sansSerifFontFamily } from 'utils/styles'
import lasso1 from '../../assets/edit-lasso1.svg'
import { EditLayoutOne } from './EditLayoutOne'

const GUTTER = 16
const PADDING_HORIZONTAL = 11
const INITIAL_PRODUCTS_NUM = 6

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    overflow: 'hidden',
    flexDirection: 'column',
    position: 'relative',
    minHeight: 531.96,
    marginBottom: 30,
  },
  background: {
    position: 'absolute',
    transform: 'translateX(-50%)',
    left: '50%',
    top: 30,
    pointerEvents: 'none',
  },
  userBackground: {
    top: 0,
  },
  backgroundFlipped: {
    transform: 'scaleX(-1) translateX(50%)',
  },
  header: {
    marginTop: 0,
    marginLeft: 21,
    marginRight: 21,
    marginBottom: 12,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    zIndex: 10,
  },
  showMoreButton: {
    color: '#CBA67A',
    fontFamily: sansSerifFontFamily,
    fontWeight: 700,
    fontSize: 12,
    lineHeight: '15px',
    letterSpacing: '0.1em',
  },
  showMoreButtoninUserProfile: {
    marginTop: 8,
  },
  editHeaderContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    position: 'relative',
  },
  productsContainer: {
    paddingLeft: PADDING_HORIZONTAL,
    paddingRight: PADDING_HORIZONTAL,
    marginBottom: 6,
  },
  stamp: {
    position: 'absolute',
  },
  stampLeft: {
    top: 0,
    left: 0,
    width: 50,
    height: 30,
  },
  stampRight: {
    top: 0,
    right: 0,
    width: 50,
    height: 15,
  },
  product: {
    marginBottom: 16,
    width: `calc(33% - ${PADDING_HORIZONTAL}px * 2 / 3 - ${GUTTER}px * 2 / 3)`,

    '@media (max-width: 376px)': {
      width: `calc(50% - ${PADDING_HORIZONTAL}px / 2 - ${GUTTER}px)`,
    },
  },
})

export type EditFeedMode = 'user' | 'combined'

interface Props {
  list: UserListPublic
  mode: EditFeedMode
  flipLasso?: boolean
  isExpanded: boolean
  onToggleExpand: () => void
}

export const Edit: FC<Props> = flowMax(
  addDisplayName('Edit'),
  addProps(({ list: { products }, isExpanded }) => ({
    products: isExpanded ? products : take(INITIAL_PRODUCTS_NUM, products),
    canExpand: products.length > INITIAL_PRODUCTS_NUM,
  })),
  addTranslationHelpers,
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    list,
    products,
    mode,
    isExpanded,
    canExpand,
    onToggleExpand,
    flipLasso,
    classes,
    t,
  }) => (
    <div className={classes.container}>
      <img
        src={lasso1}
        alt=""
        className={cx(
          classes.background,
          mode === 'user' && classes.userBackground,
          flipLasso && classes.backgroundFlipped
        )}
      />
      <div className={classes.header}>
        <div>
          {mode === 'combined' && (
            <div className={classes.editHeaderContainer}>
              <Creator creator={list.user} height={40} showUsername />
            </div>
          )}
        </div>
        {canExpand && mode === `combined` && (
          <Button onClick={onToggleExpand} className={classes.showMoreButton}>
            {isExpanded
              ? t('edits.collapse')
              : t('edits.expand', { num: list.products.length })}
          </Button>
        )}
      </div>
      <EditLayoutOne
        products={products}
        userListId={list.id}
        creatorUsername={list.user.username!}
      />
    </div>
  )
)
